import React from 'react';
import type { LucideIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from '../../lib/utils';
import { AnimatedSection } from '../shared/AnimatedSection';

type PolicyCardItem = {
  label: string;
  value: string;
  description: string;
};

type PolicyDetailItem = {
  label: string;
  value: React.ReactNode;
};

type PolicyRelatedLink = {
  label: string;
  href: string;
  external?: boolean;
};

interface PolicySectionProps {
  title: string;
  children: React.ReactNode;
  icon?: LucideIcon;
  delay?: number;
  tone?: 'default' | 'muted' | 'accent';
  className?: string;
}

const sectionToneClassNames = {
  default: 'bg-white',
  muted: 'bg-[color:var(--zw-ivory)]',
  accent: 'bg-[color:var(--zw-sand)]'
};

export function PolicyLastUpdated({ date }: { date: string }) {
  return (
    <AnimatedSection>
      <div className="mb-8 text-sm text-zwheel-gray-500">
        Última actualización: {date}
      </div>
    </AnimatedSection>
  );
}

export function PolicyCardGrid({ items }: { items: PolicyCardItem[] }) {
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
      {items.map((item) => (
        <div key={item.label} className="rounded-[1.75rem] border border-gray-200 bg-white p-6 shadow-sm">
          <p className="mb-3 text-sm font-bold uppercase tracking-[0.14em] text-gray-500">{item.label}</p>
          <p className="mb-3 text-2xl font-bold text-gray-900">{item.value}</p>
          <p className="text-sm leading-6 text-gray-600">{item.description}</p>
        </div>
      ))}
    </div>
  );
}

export function PolicySection({
  title,
  children,
  icon: Icon,
  delay = 0,
  tone = 'default',
  className
}: PolicySectionProps) {
  return (
    <AnimatedSection delay={delay}>
      <section
        className={cn(
          'rounded-[2rem] border border-gray-200 p-8 md:p-10',
          sectionToneClassNames[tone],
          className
        )}
      >
        <div className="mb-6 flex items-center gap-3">
          {Icon ? <Icon className="h-7 w-7 text-black" /> : null}
          <h2 className="text-2xl font-bold text-gray-900 md:text-3xl">{title}</h2>
        </div>
        {children}
      </section>
    </AnimatedSection>
  );
}

export function PolicyBulletList({ items }: { items: React.ReactNode[] }) {
  return (
    <ul className="space-y-4 text-base leading-7 text-gray-700">
      {items.map((item, index) => (
        <li key={index} className="flex items-start gap-3">
          <span className="mt-3 h-2 w-2 flex-shrink-0 rounded-full bg-black" aria-hidden="true" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export function PolicyDetailList({ items, className }: { items: PolicyDetailItem[]; className?: string }) {
  return (
    <dl className={cn('grid gap-5 md:grid-cols-2', className)}>
      {items.map((item) => (
        <div key={item.label} className="rounded-2xl border border-gray-200 bg-gray-50 px-5 py-4">
          <dt className="mb-2 text-sm font-bold uppercase tracking-[0.14em] text-gray-500">{item.label}</dt>
          <dd className="text-base leading-7 text-gray-700">{item.value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function PolicyRelatedLinks({
  title,
  links
}: {
  title: string;
  links: PolicyRelatedLink[];
}) {
  return (
    <AnimatedSection delay={0.3}>
      <div className="rounded-[2rem] border border-gray-200 bg-gray-50 p-8">
        <h3 className="mb-4 text-lg font-semibold text-zwheel-black">{title}</h3>
        <ul className="space-y-3">
          {links.map((link) => (
            <li key={link.href}>
              {link.external ? (
                <a
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-blue-600 underline underline-offset-4 hover:text-blue-700"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  to={link.href}
                  className="font-medium text-blue-600 underline underline-offset-4 hover:text-blue-700"
                >
                  {link.label}
                </Link>
              )}
            </li>
          ))}
        </ul>
      </div>
    </AnimatedSection>
  );
}
